import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Wrench } from 'lucide-react';

interface AnalyticsEvent {
  event_type: string;
  session_id: string;
  event_data: Record<string, unknown>;
}

interface ToolUsageTableProps {
  events: AnalyticsEvent[];
}

const PROCESSING_EVENTS = new Set([
  'compression_success', 'tool_success', 'crop_apply', 'rotate_apply',
  'adjust_apply', 'pdf_generate', 'advanced_convert',
]);

const DOWNLOAD_EVENTS = new Set([
  'download_click', 'download_success', 'batch_download_zip',
]);

// Cluster mapping for Phase 4 tools
const CLUSTER_MAP: Record<string, string> = {
  'quitar-ruido-imagen': 'Ajuste de imagen',
  'eliminar-grano-imagen': 'Ajuste de imagen',
  'mejorar-foto-borrosa': 'Ajuste de imagen',
  'aclarar-imagen-oscura': 'Ajuste de imagen',
  'mejorar-calidad-foto-online': 'Ajuste de imagen',
  'redimensionar-imagen-para-whatsapp': 'Redimensionar (uso)',
  'redimensionar-imagen-para-cv': 'Redimensionar (uso)',
  'reducir-imagen-para-cv': 'Tamaño objetivo (uso)',
  'reducir-imagen-para-formulario': 'Tamaño objetivo (uso)',
};

const getCluster = (slug: string): string => {
  if (CLUSTER_MAP[slug]) return CLUSTER_MAP[slug];
  if (slug.startsWith('convertir-')) return 'Conversión';
  if (slug.startsWith('redimensionar-')) return 'Redimensionar';
  if (slug.startsWith('reducir-')) return 'Tamaño objetivo';
  if (slug.startsWith('comprimir-')) return 'Compresión';
  if (slug.startsWith('recortar-')) return 'Recorte';
  if (slug.startsWith('rotar-')) return 'Rotación';
  return 'Otros';
};

const ToolUsageTable = ({ events }: ToolUsageTableProps) => {
  const sessionTools = new Map<string, string>();
  events.forEach(e => {
    if (e.event_type === 'page_view' && e.event_data?.page_type === 'tool_page' && e.event_data?.tool_slug) {
      sessionTools.set(e.session_id, String(e.event_data.tool_slug));
    }
    if (['tool_start', 'tool_success', 'upload_success', 'download_success'].includes(e.event_type) && e.event_data?.tool_slug) {
      if (!sessionTools.has(e.session_id)) {
        sessionTools.set(e.session_id, String(e.event_data.tool_slug));
      }
    }
  });

  const toolMetrics = new Map<string, { uploads: number; compressions: number; downloads: number }>();

  events.forEach(e => {
    const tool = sessionTools.get(e.session_id);
    if (!tool) return;

    if (!toolMetrics.has(tool)) {
      toolMetrics.set(tool, { uploads: 0, compressions: 0, downloads: 0 });
    }
    const m = toolMetrics.get(tool)!;

    if (e.event_type === 'upload_success') m.uploads++;
    else if (PROCESSING_EVENTS.has(e.event_type)) m.compressions++;
    else if (DOWNLOAD_EVENTS.has(e.event_type)) m.downloads++;
  });

  const rows = Array.from(toolMetrics.entries())
    .map(([tool, m]) => ({ tool, cluster: getCluster(tool), ...m }))
    .sort((a, b) => a.cluster.localeCompare(b.cluster) || b.compressions - a.compressions);

  // Cluster summaries
  const clusterTotals = new Map<string, { uploads: number; compressions: number; downloads: number }>();
  rows.forEach(r => {
    if (!clusterTotals.has(r.cluster)) {
      clusterTotals.set(r.cluster, { uploads: 0, compressions: 0, downloads: 0 });
    }
    const t = clusterTotals.get(r.cluster)!;
    t.uploads += r.uploads;
    t.compressions += r.compressions;
    t.downloads += r.downloads;
  });

  if (rows.length === 0) return null;

  // Build rows grouped by cluster
  const groupedRows: Array<{ type: 'cluster'; cluster: string; uploads: number; compressions: number; downloads: number } | { type: 'tool'; tool: string; cluster: string; uploads: number; compressions: number; downloads: number }> = [];
  let lastCluster = '';
  rows.forEach(r => {
    if (r.cluster !== lastCluster) {
      const totals = clusterTotals.get(r.cluster)!;
      groupedRows.push({ type: 'cluster', cluster: r.cluster, ...totals });
      lastCluster = r.cluster;
    }
    groupedRows.push({ type: 'tool', ...r });
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wrench className="w-5 h-5" />
          Uso por Herramienta
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Herramienta</TableHead>
              <TableHead className="text-right">Uploads</TableHead>
              <TableHead className="text-right">Procesos</TableHead>
              <TableHead className="text-right">Descargas</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groupedRows.map((r, i) =>
              r.type === 'cluster' ? (
                <TableRow key={`cluster-${r.cluster}`} className="bg-muted/50 font-semibold">
                  <TableCell className="text-sm">{r.cluster}</TableCell>
                  <TableCell className="text-right">{r.uploads}</TableCell>
                  <TableCell className="text-right">{r.compressions}</TableCell>
                  <TableCell className="text-right">{r.downloads}</TableCell>
                </TableRow>
              ) : (
                <TableRow key={r.tool}>
                  <TableCell className="text-sm font-mono pl-6">{r.tool}</TableCell>
                  <TableCell className="text-right">{r.uploads}</TableCell>
                  <TableCell className="text-right">{r.compressions}</TableCell>
                  <TableCell className="text-right">{r.downloads}</TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default ToolUsageTable;
